import { t } from "i18next";
import ceil from "lodash/ceil";
import { useMemo } from "react";

import { colors } from "#app/theme/v1/colors";
import { PartDto } from "#modules/parts/infra/fetchPart.api";
import { AnalyzeError } from "#modules/quoteRequestLines/AnalyzeError";
import { AnalyzeLoader } from "#modules/quoteRequestLines/AnalyzeLoader";
import { useUnavailableMaterialVolume } from "#modules/quoteRequestLines/views/useUnavailableMaterialVolume";
import { Icon } from "#shared/icons/Icon";
import { Flex } from "#shared/view/components/Flex";
import { Group } from "#shared/view/components/Group";
import { Text } from "#shared/view/components/Text";
import { Tooltip } from "#shared/view/designSystem/Tooltip";
import { getMaterialWeight } from "shared";

export const MaterialWeight = ({
  volume,
  density,
  part,
  quoteRequestLineId,
}: {
  volume: number | null;
  density: number;
  part: PartDto | undefined;
  quoteRequestLineId: string;
}) => {
  const { displayWarning, isLoading, onRelaunchAnalyze } = useUnavailableMaterialVolume({
    volume,
    part,
    quoteRequestLineId,
  });

  const weight = useMemo(() => ceil(getMaterialWeight(volume ?? 0, density), 3), [volume, density]);

  if (displayWarning && isLoading) {
    return (
      <Flex gap="xs" align="center">
        <Icon name="weight" size={16} />
        <AnalyzeLoader text={t("qrl.analyze.analyzing")} />
      </Flex>
    );
  }

  if (displayWarning) {
    return (
      <Flex gap="xs" align="center">
        <Icon name="weight" size={16} />
        <AnalyzeError
          retryAction={onRelaunchAnalyze}
          retryText={t("qrl.analyze.relaunch")}
          errorText={t("qrl.noVolume")}
          justify="start"
        />
      </Flex>
    );
  }

  return (
    <Group gap="xxxs" align="center">
      <Text>
        {t("qrl.materialWeight")} : <b>{`${weight} kg`}</b>
      </Text>
      {weight === 0 && (
        <Tooltip label={t("qrl.materialWeightZeroWarning")}>
          <Icon name="alertTriangle" size={16} color={colors.orange[7]} style={{ marginLeft: 4 }} />
        </Tooltip>
      )}
    </Group>
  );
};
